import { AllowedEvent } from '@fhs-legacy/frontend/src/state/crm-events/types';

import { PaymentOrchestrator, PaymentPipelineError, PaymentPipelineResult } from '../../../payment';
import { usePaymentFlowStore } from '../../../state/payment-stores/payment-flow-store';

const DIGITAL_WALLET_ERROR_CODES = new Set([
  // Google Pay errors
  'GOOGLE_PAY_CANCELLED',
  'GOOGLE_PAY_NO_DATA',
  'GOOGLE_PAY_REQUEST_FAILED',
  'GOOGLE_PAY_ERROR',
  'GOOGLE_PAY_API_NOT_LOADED',
  'GOOGLE_PAY_CONFIG_MISSING',
  // Apple Pay errors
  'APPLE_PAY_ERROR',
  'APPLE_PAY_NOT_AVAILABLE',
  'APPLE_PAY_NO_CARDS',
  'APPLE_PAY_AVAILABILITY_CHECK_FAILED',
  'APPLE_PAY_CONFIG_MISSING',
  'APPLE_PAY_API_NOT_LOADED',
  'APPLE_PAY_NOT_SUPPORTED',
]);

interface HandlePlaceOrderDependencies {
  lastResultRef: React.MutableRefObject<PaymentPipelineResult | null>;
  orchestratorRef: React.MutableRefObject<PaymentOrchestrator>;
  buildPipelineContext: () => any;
  setShouldSkipPriceOrder: (skip: boolean) => void;
  setIsProcessing: (processing: boolean) => void;
  setCurrentError: (error: PaymentPipelineError | null) => void;
  logRBIEvent: (event: AllowedEvent) => void;
}

function isDigitalWalletError(errorCode: string | undefined): boolean {
  return !!errorCode && DIGITAL_WALLET_ERROR_CODES.has(errorCode);
}

// Shared across the Place Order button and gift-card reload sheet so a second entry cannot
// start another commit while one is already running.
let isPlaceOrderInFlight = false;

export async function handlePlaceOrder({
  lastResultRef,
  orchestratorRef,
  setCurrentError,
  setIsProcessing,
  setShouldSkipPriceOrder,
  buildPipelineContext,
  logRBIEvent,
}: HandlePlaceOrderDependencies) {
  if (isPlaceOrderInFlight) {
    return;
  }
  isPlaceOrderInFlight = true;

  const { setRetryInfo, setCurrentState } = usePaymentFlowStore.getState();
  const lastResult = lastResultRef.current;
  const isRetry = !!lastResult?.error?.retryFromStep;
  let orderSubmitted = false;

  orchestratorRef.current.setCRMLogger(logRBIEvent);

  try {
    if (!isRetry && !getPipelineContext(buildPipelineContext, setCurrentError)) {
      return;
    }

    setCurrentError(null);
    setIsProcessing(true);
    setRetryInfo(null);
    setCurrentState('idle');

    const needsFreshContext =
      isRetry &&
      (lastResult.error?.code === 'MISSING_ENCRYPTION_RESULT' ||
        lastResult.error?.code === 'COMMIT_ORDER_FAILED' ||
        lastResult.error?.code === 'ENCRYPTION_FAILED');

    let result: PaymentPipelineResult;
    if (
      isRetry &&
      !needsFreshContext &&
      lastResult != null &&
      lastResult.context != null &&
      lastResult.error != null &&
      lastResult.error.retryFromStep != null
    ) {
      result = await orchestratorRef.current.execute(lastResult.context, {
        skipToStep: lastResult.error.retryFromStep,
      });
    } else {
      const pipelineContext = getPipelineContext(buildPipelineContext, setCurrentError);
      if (!pipelineContext) {
        return;
      }
      result = await orchestratorRef.current.execute(pipelineContext);
    }

    lastResultRef.current = result;

    if (result.success) {
      orderSubmitted = true;
      handleSuccess(setShouldSkipPriceOrder, setRetryInfo, setCurrentError);
      return;
    }

    setCurrentState('error');
    handleError(result, setCurrentError, setRetryInfo);

    if (isDigitalWalletError(result.error?.code)) {
      lastResultRef.current = null;
    }
  } catch (error) {
    handleUnexpectedError(error, setCurrentError, setRetryInfo);
  } finally {
    isPlaceOrderInFlight = false;
    if (!orderSubmitted) {
      setIsProcessing(false);
    }
  }
}

function getPipelineContext(
  buildPipelineContext: HandlePlaceOrderDependencies['buildPipelineContext'],
  setCurrentError: HandlePlaceOrderDependencies['setCurrentError']
) {
  const pipelineContext = buildPipelineContext();

  if (!pipelineContext) {
    setCurrentError({
      code: 'CONTEXT_NOT_READY',
      message: 'Pipeline context is not ready',
      userMessage:
        'Unable to process payment. Please check your payment details and try again. Error Code: (Pipeline Context Readiness)',
    });
    return null;
  }

  return pipelineContext;
}

function handleSuccess(
  setShouldSkipPriceOrder: (skip: boolean) => void,
  setRetryInfo: (info: any) => void,
  setCurrentError: (error: PaymentPipelineError | null) => void
) {

  setShouldSkipPriceOrder(true);
  setRetryInfo(null);
  setCurrentError(null);
}

function handleError(
  result: PaymentPipelineResult,
  setCurrentError: (error: PaymentPipelineError | null) => void,
  setRetryInfo: (info: any) => void
) {
  setCurrentError(result.error ?? null);

  const errorCode = result.error?.code;

  if (isDigitalWalletError(errorCode)) {
    return;
  }

  if (result.error?.retryFromStep) {
    setRetryInfo({
      canRetry: true,
      retryFromStep: result.error.retryFromStep,
      errorCode: result.error.code,
      errorMessage: result.error.message,
      failedStep: result.failedStep,
    });
  }
}

function handleUnexpectedError(
  error: unknown,
  setCurrentError: (error: PaymentPipelineError | null) => void,
  setRetryInfo: (info: any) => void
) {
  setCurrentError({
    code: 'UNEXPECTED_ERROR',
    message: error instanceof Error ? error.message : 'Unknown error',
    userMessage:
      'An unexpected error occurred. Please try again. Error Code: (Unexpected Orchestrator Error)',
  });
  setRetryInfo(null);
}
